import { CustomSelectItem } from '@hwm/ui-lib';
import { FormFieldFG, FormFieldTypeFG, SelectFormFieldFG } from '@hwm/ui-lib/ui/components';
import { t } from '@lingui/macro';
import { AccountModel } from '@shared/models';
import { object, string } from 'yup';

export enum PMZ_FORM_FIELD_NAMES {
  accNameField = 'name',
  descriptionField = 'description',
  parentIdField = 'parentId',
  parentField = 'parent',
}

export enum CreateEditPMZoneErrorCode {
  AccountNameNotUnique = 'Account.AccountNameNotUnique',
}

export const CreateEditPMZoneErrorCodeMessage: Record<CreateEditPMZoneErrorCode, string> = {
  [CreateEditPMZoneErrorCode.AccountNameNotUnique]: t`Account name is already in use. Try different one`,
};

const { accNameField, descriptionField, parentField, parentIdField } = PMZ_FORM_FIELD_NAMES;

export const createPMZoneFormValidationSchemaFn = () =>
  object().shape({
    [accNameField]: string()
      .required(t({ message: 'Field is required', id: '[Validation.FieldRequired]' }))
      .min(
        3,
        t({
          message: `Account name should be longer than ${3} characters`,
          id: '[Validation.AccNameShouldBeLongerThan]',
        })
      )
      .max(
        100,
        t({
          message: `Account name should not be longer than ${100} characters`,
          id: '[Validation.AccNameShouldNotBeLongerThan]',
        })
      ),
    [descriptionField]: string()
      .required(t({ message: 'Field is required', id: '[Validation.FieldRequired]' }))
      .min(
        3,
        t({
          message: `Description should be longer than ${3} characters`,
          id: '[Validation.DescriptionShouldBeLongerThan]',
        })
      )
      .max(
        100,
        t({
          message: `Description should not be longer than ${100} characters`,
          id: '[Validation.DescriptionShouldNotBeLongerThan]',
        })
      ),
  });

export type PMZoneModalTypes = 'create' | 'edit';

export interface CreateEditPMZoneRequest {
  [accNameField]: string;
  [descriptionField]: string;
  [parentIdField]?: number;
}

export interface CreatePMZoneFields {
  [accNameField]: string;
  [descriptionField]: string;
  [parentField]: CustomSelectItem;
}

export const zoneFormInitialValues: CreatePMZoneFields = {
  [accNameField]: '',
  [descriptionField]: '',
  [parentField]: undefined,
};

const tailwindClasses = {
  container: 'flex justify-between flex-wrap mb-2 sm:mb-4 items-start sm:items-center flex-col sm:flex-row',
};

export const fieldsConfiguration = (show: boolean): FormFieldFG[] => [
  {
    label: t({ message: 'Account Name', id: '[FormField.AccountName]' }),
    property: accNameField,
    type: FormFieldTypeFG.Input,
    containerClasses: tailwindClasses.container,
    isDisabled: !show,
  },
  {
    label: t({ message: 'Parent Account', id: '[FormField.ParentAccount]' }),
    property: parentField,
    availableValues: [],
    type: FormFieldTypeFG.Select,
    containerClasses: tailwindClasses.container,
    isDisabled: true,
  } as SelectFormFieldFG,
  {
    label: t`Description`,
    property: descriptionField,
    type: FormFieldTypeFG.TextArea,
    containerClasses: 'flex flex-col mb-4',
    isDisabled: !show,
  },
];

export const formInitialValues = (
  typeOfForm: PMZoneModalTypes,
  account: AccountModel,
  hierarchy: AccountModel[]
): CreatePMZoneFields => {
  if (typeOfForm === 'create') {
    return {
      ...zoneFormInitialValues,
      [parentField]: {
        label: account.name,
        value: account.id,
      },
    };
  } else {
    return {
      ...zoneFormInitialValues,
      [accNameField]: account.name,
      [descriptionField]: account.description,
      [parentField]:
        hierarchy.length > 1
          ? {
              label: hierarchy[hierarchy.length - 2].name,
              value: hierarchy[hierarchy.length - 2].id,
            }
          : undefined,
    };
  }
};

export const mapFormFieldsToRequest = (values: CreatePMZoneFields): CreateEditPMZoneRequest => {
  return {
    [accNameField]: values[accNameField],
    [descriptionField]: values[descriptionField],
    [parentIdField]: values[parentField].value as number,
  };
};
